import { memo, useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { cn } from "@/shared/utils/general/cn"
import { addTask } from "@/db/operations"
import { pasteTasksFromText } from "@/shared/utils/task/pasteTasks"
import { useViewContext } from "@/app/stores/viewContext"
import { getTaskDefaults } from "@/shared/utils/task/getTaskDefaults"
import { useSetting } from "@/features/settings/hooks/useSetting"

interface QuickAddProps {
  /** Auto-focus the input on mount */
  autoFocus?: boolean
  className?: string
}

/**
 * Quick-add task input — appears at the top of each view.
 *
 * Behavior:
 * - Enter: creates task, clears input, keeps focus
 * - Escape: clears input, blurs
 * - Uses getTaskDefaults() to inherit scheduling context from the current view
 */
export const QuickAdd = memo(function QuickAdd({
  autoFocus,
  className,
}: QuickAddProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState("")
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const viewId = useViewContext((s) => s.viewId)
  const containerId = useViewContext((s) => s.containerId)
  const { value: eveningHour } = useSetting<number>("#evening-time", 18)

  const handleSubmit = useCallback(async () => {
    const title = value.trim()
    if (!title) return

    const defaults = getTaskDefaults(
      viewId,
      containerId ?? undefined,
      eveningHour,
    )

    // Create the task at the TOP of the list so it appears right below QuickAdd.
    // This matches Things 3 behavior — immediate visual feedback for rapid entry.
    const result = await addTask({
      title,
      parentPath: defaults.parentPath,
      insertPosition: "top",
      startAt: defaults.schedule?.startAt,
      startAtType: defaults.schedule?.type,
      startAtSubType: defaults.schedule?.subType,
    })
    if (!result.ok) return

    setValue("")
    // Keep focus for rapid entry
    inputRef.current?.focus()
  }, [value, viewId, containerId, eveningHour])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault()
        handleSubmit()
      } else if (e.key === "Escape") {
        setValue("")
        inputRef.current?.blur()
      }
    },
    [handleSubmit],
  )

  // §17a — Multi-line paste: each line becomes a separate task
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const text = e.clipboardData.getData("text/plain")
      if (!text) return

      const lines = text
        .split(/\r?\n/)
        .map((l) => l.replace(/^[-*•]\s*(\[.\]\s*)?/, "").trim())
        .filter(Boolean)

      if (lines.length <= 1) return // Single line — let default paste work

      e.preventDefault()

      // Shared paste path: atomic, ordered, one toast
      void pasteTasksFromText(text, { viewId, containerId, eveningHour }).catch(
        (error) => {
          console.error("[QuickAdd] Paste failed:", error)
          toast.error(t("Failed to paste tasks"))
        },
      )

      setValue("")
    },
    [viewId, containerId, eveningHour, t],
  )

  return (
    <div
      data-quick-add
      className={cn(
        "group relative flex min-h-11 items-center gap-2 rounded-lg px-3 py-2",
        "select-none transition-colors",
        isFocused ? "bg-muted/50" : "md:hover:bg-muted/50",
        className,
      )}
    >
      {/* Ghost checkbox — matches TaskCheckbox medium exactly (20×20, 1.5px border, 5px radius) */}
      <span
        className="shrink-0 border-muted-foreground/20"
        style={{
          display: "block",
          width: 20,
          height: 20,
          borderWidth: 1.5,
          borderStyle: "dashed",
          borderRadius: 5,
        }}
      />

      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={t("New task…")}
        autoFocus={autoFocus}
        className={cn(
          "min-w-0 flex-1 bg-transparent text-base font-semibold outline-none",
          "placeholder:font-normal placeholder:text-muted-foreground/30",
        )}
      />

      {/* Submit button — visible when there's text */}
      {value.trim() && (
        <button
          onClick={handleSubmit}
          className="shrink-0 rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          {t("Add")}
        </button>
      )}
    </div>
  )
})
